import { ip_back } from "../config";

const STORAGE_KEY = "smarttt.signedMedia.v1";
/** Refresh before the signature actually expires so playback never starts on a dead URL. */
const REFRESH_SKEW_MS = 2 * 60 * 1000;

/** object URL (no query) → { url, expiresAt } */
const memory = new Map();
const inflight = new Map();
let storeLoaded = false;

export function isTtdataUrl(url) {
  try {
    const host = new URL(url).hostname.toLowerCase();
    return (
      host === "s3.gra.io.cloud.ovh.net" ||
      host.endsWith(".s3.gra.io.cloud.ovh.net")
    );
  } catch {
    return false;
  }
}

/** True when playback must wait for /api/media/signed-url before setting <video src>. */
export function needsSignedMediaUrl(url) {
  return isTtdataUrl(url);
}

export function isAlreadySignedMediaUrl(url) {
  return /[?&]X-Amz-Signature=/i.test(String(url || ""));
}

function objectKey(url) {
  return String(url || "").trim().split("#")[0].split("?")[0];
}

function expiryFromSignedUrl(signed) {
  try {
    const params = new URL(signed).searchParams;
    const exp = Number(
      params.get("X-Amz-Expires") || params.get("x-amz-expires")
    );
    const stamp =
      params.get("X-Amz-Date") || params.get("x-amz-date") || "";
    const iso = stamp.replace(
      /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z$/,
      "$1-$2-$3T$4:$5:$6Z"
    );
    const start = Date.parse(iso);
    if (Number.isFinite(exp) && exp > 0 && Number.isFinite(start)) {
      return start + exp * 1000;
    }
  } catch {
    /* fall through */
  }
  return Date.now() + 50 * 60 * 1000;
}

function loadStore() {
  if (storeLoaded || typeof sessionStorage === "undefined") return;
  storeLoaded = true;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return;
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return;
    const now = Date.now();
    for (const [key, entry] of Object.entries(parsed)) {
      if (!entry?.url || !entry.expiresAt) continue;
      if (entry.expiresAt <= now + REFRESH_SKEW_MS) continue;
      if (!/X-Amz-Signature=/i.test(entry.url)) continue;
      memory.set(key, entry);
    }
  } catch {
    /* ignore quota / private mode */
  }
}

function persistStore() {
  if (typeof sessionStorage === "undefined") return;
  try {
    const now = Date.now();
    const obj = {};
    for (const [key, entry] of memory) {
      if (entry?.expiresAt > now) obj[key] = entry;
    }
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(obj));
  } catch {
    /* ignore quota */
  }
}

/** Keep a signature the caller already has (replay review, a previous play). */
export function rememberSignedMediaUrl(signedUrl) {
  const signed = String(signedUrl || "").trim();
  if (!/X-Amz-Signature=/i.test(signed)) return;
  const key = objectKey(signed);
  if (!key || !isTtdataUrl(key)) return;
  remember(key, signed);
}

/**
 * URL safe to put on <video>. Never the bare private object URL.
 * `previous` is kept when it is already a signature for the same object.
 */
export function playableMediaSrc(url, previous) {
  const next = String(url || "").trim();
  if (!next) return null;
  if (!isTtdataUrl(next)) return next;
  if (/X-Amz-Signature=/i.test(next)) return next;
  const cached = peekSignedMediaUrl(next);
  if (cached) return cached;
  const prev = String(previous || "");
  if (
    prev.split("?")[0] === next.split("?")[0] &&
    /X-Amz-Signature=/i.test(prev)
  ) {
    return prev;
  }
  return null;
}

/** Already-signed URL for this object, or null. Does not hit the network. */
export function peekSignedMediaUrl(url) {
  loadStore();
  const key = objectKey(url);
  if (!key || !isTtdataUrl(key)) return null;
  const entry = memory.get(key);
  if (!entry?.url || entry.expiresAt <= Date.now() + REFRESH_SKEW_MS) {
    return null;
  }
  return entry.url;
}

function remember(key, signed) {
  if (!key || !signed || !/X-Amz-Signature=/i.test(signed)) return;
  memory.set(key, { url: signed, expiresAt: expiryFromSignedUrl(signed) });
  persistStore();
}

async function fetchSigned(cacheKey) {
  const token =
    (typeof localStorage !== "undefined" && localStorage.getItem("token")) ||
    "";
  const resp = await fetch(
    `${ip_back}api/media/signed-url?url=${encodeURIComponent(cacheKey)}`,
    { headers: token ? { Authorization: token } : {} }
  );
  const json = await resp.json().catch(() => ({}));
  const signed = String(json?.data?.url || "").trim();
  if (!signed || !/X-Amz-Signature=/i.test(signed)) return cacheKey;
  remember(cacheKey, signed);
  return signed;
}

export async function resolveSignedMediaUrl(url, { force = false } = {}) {
  const raw = String(url || "").trim();
  if (!raw || !isTtdataUrl(raw)) return raw;
  const cacheKey = objectKey(raw);
  if (!force) {
    const hit = peekSignedMediaUrl(cacheKey);
    if (hit) return hit;
    const pending = inflight.get(cacheKey);
    if (pending) return pending;
  } else {
    memory.delete(cacheKey);
    persistStore();
  }
  const job = fetchSigned(cacheKey).finally(() => {
    inflight.delete(cacheKey);
  });
  inflight.set(cacheKey, job);
  return job;
}

/** Sign a list of clips in the background so opening one does not wait. */
export function prefetchSignedMediaUrls(urls) {
  const list = Array.isArray(urls) ? urls : [];
  for (const url of list) {
    if (!isTtdataUrl(url)) continue;
    if (peekSignedMediaUrl(url)) continue;
    resolveSignedMediaUrl(url).catch(() => {});
  }
}

/** Drop a cached signature (e.g. after expiry or a failed play). */
export function clearSignedMediaUrlCache(url) {
  const raw = objectKey(url);
  if (raw) memory.delete(raw);
  persistStore();
}
